import React, { useState, useRef, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import StarRating from '../components/StarRating';
import LoadingSpinner from '../components/LoadingSpinner';
import PhotoGallery from '../components/PhotoGallery';
import AddToListModal from '../components/AddToListModal';
import WantToTryButton from '../components/WantToTryButton';
import FeatureVoting from '../components/FeatureVoting';
import CheckInButton from '../components/CheckInButton';
import ShareReviewModal from '../components/ShareReviewModal';
import { computeOverallRating, hasAnySubRating } from '../utils/ratings';
import ReportModal from '../components/ReportModal';
import { trackEvent } from '../lib/posthog';
import { getBarLocationLabel } from '../utils/barLocation';
import { getFeaturesForMetro } from '../data/features';
import {
  ArrowLeft,
  MapPin,
  Star,
  DollarSign,
  Martini,
  Users,
  Zap,
  MessageCircle,
  Send,
  Beer,
  Crosshair,
  Music,
  Sun,
  Clock,
  CreditCard,
  CheckCircle2,
  LogIn,
  List,
  Wine,
  Info,
  Share2,
  Flag,
  Camera,
  Popcorn,
  X,
  Globe,
  Loader2,
  Upload,
  Image
} from 'lucide-react';

const MAX_PHOTOS_PER_RATING = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function BarDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const {
    bars,
    comments,
    getBarStats,
    getUserRating,
    addRating,
    addComment,
    deleteComment,
    uploadPhoto,
    loading,
    blockedUsers
  } = useApp();

  const photoInputRef = useRef(null);
  const ratingModalRef = useRef(null);
  
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [lastSubmittedRating, setLastSubmittedRating] = useState(null);

  // Track bar view (fire once when bar data is available)
  const barViewTracked = useRef(null);
  useEffect(() => {
    const bar = bars.find(b => b.id === id);
    if (bar && barViewTracked.current !== id) {
      barViewTracked.current = id;
      trackEvent('bar_viewed', {
        bar_id: bar.id,
        bar_name: bar.name,
        neighborhood: bar.neighborhood,
      });
    }
  }, [id, bars]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lock body scroll when modal is open + PWA scroll fix
  useEffect(() => {
    if (showRatingModal) {
      document.body.style.overflow = 'hidden';
      // PWA scroll fix: force modal scroll to top
      if (ratingModalRef.current) {
        ratingModalRef.current.scrollTop = 0;
      }
      // Delayed attempts for PWA which can be slow to render
      const timer1 = setTimeout(() => {
        if (ratingModalRef.current) {
          ratingModalRef.current.scrollTop = 0;
        }
      }, 50);
      const timer2 = setTimeout(() => {
        if (ratingModalRef.current) {
          ratingModalRef.current.scrollTop = 0;
        }
      }, 150);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [showRatingModal]);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showDiveBarInfo, setShowDiveBarInfo] = useState(false);
  const [reportingComment, setReportingComment] = useState(null);

  // Rating form state (now includes drinkSelection)
  const [ratingForm, setRatingForm] = useState({
    overall: 0,
    price: 0,
    ambiance: 0,
    crowd: 0,
    service: 0,
    drinkSelection: 0,
    isDiveBar: false
  });

  // Photos to upload with rating
  const [ratingPhotos, setRatingPhotos] = useState([]);
  const [photoError, setPhotoError] = useState('');
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  // Review state for rating modal (submitted with rating)
  const [modalReviewText, setModalReviewText] = useState('');
  const [modalReviewVisibility, setModalReviewVisibility] = useState('public');

  // Add photos to rating
  const handleRatingPhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    setPhotoError('');
    
    // Check total count
    const remainingSlots = MAX_PHOTOS_PER_RATING - ratingPhotos.length;
    if (files.length > remainingSlots) {
      setPhotoError(`You can only add ${remainingSlots} more photo(s)`);
      return;
    }
    
    const validFiles = [];
    const errors = [];
    
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`"${file.name}" is too large (${formatFileSize(file.size)})`);
        continue;
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`"${file.name}" is not a supported format`);
        continue;
      }
      
      validFiles.push({
        file,
        preview: URL.createObjectURL(file),
        caption: '',
        visibility: 'public',
        id: `${file.name}-${Date.now()}-${Math.random()}`
      });
    }
    
    if (errors.length > 0) {
      setPhotoError(errors.join('. '));
    }
    
    if (validFiles.length > 0) {
      setRatingPhotos(prev => [...prev, ...validFiles]);
    }
    
    // Reset input
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const removeRatingPhoto = (photoId) => {
    setRatingPhotos(prev => {
      const removed = prev.find(p => p.id === photoId);
      if (removed) URL.revokeObjectURL(removed.preview);
      return prev.filter(p => p.id !== photoId);
    });
  };

  const updateRatingPhotoVisibility = (photoId, visibility) => {
    setRatingPhotos(prev => 
      prev.map(p => p.id === photoId ? { ...p, visibility } : p)
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const bar = bars.find(b => b.id === id);

  if (!bar) {
    return (
      <div className="min-h-screen bg-dive-black flex items-center justify-center">
        <div className="text-center">
          <img src="/logo-icon.png" alt="" className="w-16 h-16 object-contain mx-auto mb-4 opacity-50" />
          <h2 className="text-white text-xl mb-2">Bar not found</h2>
          <Link to="/list" className="text-dive-neon hover:underline">
            Back to list
          </Link>
        </div>
      </div>
    );
  }

  const stats = getBarStats(bar.id);
  const userRating = getUserRating(bar.id);
  // Only show public comments on bar page (friends-only comments appear in friend feeds)
  const barComments = comments.filter(c => c.barId === bar.id && (c.visibility === 'public' || !c.visibility) && !blockedUsers.includes(c.userId));
  const isDiveBar = stats.divePercentage > 50;

  const handleDeleteComment = async (commentId) => {
    if (!confirm('Delete this comment? This cannot be undone.')) return;
    
    try {
      await deleteComment(commentId);
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const handleSubmitRating = async () => {
    if (!user || !profile) {
      setError('Please sign in to rate bars');
      return;
    }

    const derivedOverall = computeOverallRating(ratingForm);
    if (!hasAnySubRating(ratingForm) || derivedOverall === 0) {
      setError('Please rate at least one category');
      return;
    }

    // Validate modal review if user started typing
    if (modalReviewText.trim().length > 0 && modalReviewText.trim().length < 10) {
      setError('Review must be at least 10 characters');
      return;
    }
    
    setIsSubmitting(true);
    setError('');

    try {
      // Submit the rating first
      const ratingResult = await addRating({
        barId: bar.id,
        ...ratingForm,
        overall: derivedOverall
      });
      
      // Upload photos if any
      if (ratingPhotos.length > 0) {
        setUploadingPhotos(true);
        const photoErrors = [];
        
        for (const photoData of ratingPhotos) {
          try {
            await uploadPhoto(
              bar.id, 
              photoData.file, 
              photoData.caption, 
              photoData.visibility,
              ratingResult?.id // Link to rating if available
            );
          } catch (err) {
            photoErrors.push(`Failed to upload "${photoData.file.name}"`);
          }
        }
        
        setUploadingPhotos(false);
        
        if (photoErrors.length > 0) {
          console.warn('Some photos failed to upload:', photoErrors);
        }
        
        // Clean up previews
        ratingPhotos.forEach(p => URL.revokeObjectURL(p.preview));
        setRatingPhotos([]);
      }

      // Submit review if provided (optional)
      if (modalReviewText.trim().length >= 10) {
        console.log('=== SUBMITTING REVIEW WITH RATING ===');
        console.log('Review text:', modalReviewText);
        console.log('Visibility:', modalReviewVisibility);
        console.log('Bar ID:', bar.id);
        
        try {
          await addComment(bar.id, modalReviewText.trim(), modalReviewVisibility, true);
          console.log('Review submitted successfully');
        } catch (reviewErr) {
          console.error('Review submission failed:', reviewErr);
          // Don't fail the whole rating if review fails
        }
      }
      
      setShowRatingModal(false);
      // Store the rating for sharing and show share modal (include ID from Supabase result)
      setLastSubmittedRating({ ...ratingForm, overall: derivedOverall, id: ratingResult?.id });
      setShowShareModal(true);
      trackEvent('bar_rated', {
        bar_id: bar.id,
        bar_name: bar.name,
        overall_score: derivedOverall,
      });
      
      // Reset modal review state
      setModalReviewText('');
      setModalReviewVisibility('public');
    } catch (err) {
      console.error('Error submitting rating:', err);
      setError(err.message || 'Failed to submit rating');
    } finally {
      setIsSubmitting(false);
      setUploadingPhotos(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user || !profile) {
      setError('Please sign in to comment');
      return;
    }
    if (!newComment.trim()) return;
    
    setIsSubmitting(true);
    setError('');

    try {
      await addComment(bar.id, newComment.trim());
      setNewComment('');
    } catch (err) {
      console.error('Error submitting comment:', err);
      setError(err.message || 'Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openRatingModal = () => {
    if (userRating) {
      setRatingForm({
        overall: userRating.overall,
        price: userRating.price || 0,
        ambiance: userRating.ambiance || 0,
        crowd: userRating.crowd || 0,
        service: userRating.service || 0,
        drinkSelection: userRating.drinkSelection || 0,
        isDiveBar: userRating.isDiveBar
      });
    } else {
      setRatingForm({
        overall: 0,
        price: 0,
        ambiance: 0,
        crowd: 0,
        service: 0,
        drinkSelection: 0,
        isDiveBar: false
      });
    }
    setError('');
    setShowRatingModal(true);
  };

  // Auto-open rating modal when arriving from a share link with ?rate=true
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('rate') === 'true' && user && bar && !loading) {
        const timer = setTimeout(() => {
          openRatingModal();
          // Remove the query param so refreshing doesn't re-open
          window.history.replaceState({}, '', window.location.pathname);
        }, 500);
        return () => clearTimeout(timer);
      }
    } catch (e) {
      console.error('Auto-open rating modal error:', e);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, bar, loading]);

  const attributes = [
    { key: 'poolTable', label: 'Pool Table', icon: Crosshair },
    { key: 'darts', label: 'Darts', icon: Crosshair },
    { key: 'cashOnly', label: 'Cash Only', icon: CreditCard },
    { key: 'jukebox', label: 'Jukebox', icon: Music },
    { key: 'outdoorSeating', label: 'Patio', icon: Sun },
    { key: 'liveMusic', label: 'Live Music', icon: Music },
    { key: 'happyHour', label: 'Happy Hour', icon: Clock },
    { key: 'touchTunes', label: 'TouchTunes', icon: Music },
    { key: 'triviaNights', label: 'Trivia Nights', icon: Star },
    { key: 'euchre', label: 'Euchre', icon: Users },
    { key: 'oldStyleSign', label: 'Old Style Sign', icon: Beer },
    { key: 'photoBooth', label: 'Photo Booth', icon: Camera },
    { key: 'popcorn', label: 'Popcorn', icon: Popcorn }
  ].filter((attribute) => getFeaturesForMetro(bar.metroId).some((feature) => feature.key === attribute.key));

  return (
    <div className="min-h-screen bg-dive-black pb-24">
      {/* Header */}
      <div className="bg-gradient-to-b from-dive-neon/20 to-dive-black">
        <div className="max-w-4xl mx-auto p-4">
          <Link
            to="/list"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to list</span>
          </Link>

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="font-display text-3xl md:text-4xl text-white tracking-wide mb-2">
                {bar.name}
              </h1>
              <div className="flex items-center gap-2 text-gray-400 mb-4">
                <MapPin className="w-4 h-4" />
                <span>{bar.address} • {getBarLocationLabel(bar)}</span>
              </div>

              {isDiveBar && (
                <div className="inline-flex items-center gap-2">
                  <div className="flex items-center gap-2 bg-dive-neon/20 text-dive-neon px-3 py-1.5 rounded-full text-sm">
                    <CheckCircle2 className="w-4 h-4" />
                    Community Rated Dive Bar ({Math.round(stats.divePercentage)}%)
                  </div>
                  <button
                    onClick={() => setShowDiveBarInfo(!showDiveBarInfo)}
                    className="text-gray-400 hover:text-white"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </div>
              )}

              {showDiveBarInfo && (
                <div className="mt-2 text-sm text-gray-400 bg-dive-dark border border-dive-gray rounded-lg p-3">
                  <strong className="text-white">Community Rated Dive Bar</strong> means more than 50% of users have voted this bar as a dive bar.
                </div>
              )}
            </div>

            <div className="text-right">
              <div className="flex items-center gap-2 justify-end mb-1">
                <StarRating rating={stats.overall} size="md" />
              </div>
              <div className="text-3xl font-bold text-dive-amber">
                {stats.overall > 0 ? stats.overall.toFixed(1) : '—'}
              </div>
              <div className="text-gray-500 text-sm">
                {stats.totalRatings} {stats.totalRatings === 1 ? 'rating' : 'ratings'}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          {user && (
            <div className="flex gap-2 mt-4">
              <WantToTryButton barId={bar.id} />
              <button
                onClick={() => setShowListModal(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-dive-dark text-gray-400 border border-dive-gray hover:border-gray-500 hover:text-white transition-colors"
              >
                <List className="w-5 h-5" />
                <span className="text-sm">Add to List</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4">
        {/* Rate & Share Buttons - Primary actions first */}
        {user ? (
          <div className="mb-6">
            {userRating && <CheckInButton bar={bar} compact />}
            <div className="flex gap-2">
            <button
              onClick={openRatingModal}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              <Star className="w-5 h-5" />
              {userRating ? 'Update Rating' : 'Rate This Bar'}
            </button>
            {userRating && (
              <button
                onClick={() => setShowShareModal(true)}
                className="btn-secondary flex items-center justify-center gap-2 px-4"
              >
                <Share2 className="w-5 h-5" />
                Share
              </button>
            )}
            </div>
          </div>
        ) : (
          <Link
            to="/auth"
            className="btn-primary w-full mb-6 flex items-center justify-center gap-2"
          >
            <LogIn className="w-5 h-5" />
            Sign In to Rate
          </Link>
        )}

        {/* Sub-ratings (Price, Vibe, Crowd, Service, Drinks) */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <div className="bg-dive-dark border border-dive-gray rounded-xl p-4 text-center">
            <DollarSign className="w-6 h-6 text-dive-green mx-auto mb-2" />
            <div className="text-2xl font-bold text-white mb-1">
              {stats.price > 0 ? stats.price.toFixed(1) : '—'}
            </div>
            <div className="text-gray-500 text-sm">Price</div>
          </div>
          <div className="bg-dive-dark border border-dive-gray rounded-xl p-4 text-center">
            <Martini className="w-6 h-6 text-dive-blue mx-auto mb-2" />
            <div className="text-2xl font-bold text-white mb-1">
              {stats.ambiance > 0 ? stats.ambiance.toFixed(1) : '—'}
            </div>
            <div className="text-gray-500 text-sm">Ambiance</div>
          </div>
          <div className="bg-dive-dark border border-dive-gray rounded-xl p-4 text-center">
            <Users className="w-6 h-6 text-dive-amber mx-auto mb-2" />
            <div className="text-2xl font-bold text-white mb-1">
              {stats.crowd > 0 ? stats.crowd.toFixed(1) : '—'}
            </div>
            <div className="text-gray-500 text-sm">Crowd</div>
          </div>
          <div className="bg-dive-dark border border-dive-gray rounded-xl p-4 text-center">
            <Zap className="w-6 h-6 text-dive-neon mx-auto mb-2" />
            <div className="text-2xl font-bold text-white mb-1">
              {stats.service > 0 ? stats.service.toFixed(1) : '—'}
            </div>
            <div className="text-gray-500 text-sm">Service</div>
          </div>
          <div className="bg-dive-dark border border-dive-gray rounded-xl p-4 text-center col-span-2 md:col-span-1">
            <Wine className="w-6 h-6 text-purple-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-white mb-1">
              {stats.drinkSelection > 0 ? stats.drinkSelection.toFixed(1) : '—'}
            </div>
            <div className="text-gray-500 text-sm">Drinks</div>
          </div>
        </div>

        {/* Attributes */}
        <div className="mb-6">
          <h3 className="text-white font-medium mb-3 flex items-center gap-2">
            <img src="/logo-icon.png" alt="" className="w-5 h-5 object-contain" />
            Bar Features
          </h3>
          <div className="flex flex-wrap gap-2">
            {attributes.map(({ key, label, icon: Icon }) => (
              <div
                key={key}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                  bar.attributes?.[key]
                    ? 'bg-dive-neon/20 text-dive-neon border border-dive-neon/50'
                    : 'bg-dive-gray/30 text-gray-500 border border-dive-gray'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Photo Gallery */}
        <div className="mb-10">
          <PhotoGallery barId={bar.id} barName={bar.name} />
        </div>

        {/* Community Feature Voting */}
        <div className="mb-10">
          <FeatureVoting barId={bar.id} barAttributes={bar.attributes || {}} metroId={bar.metroId} />
        </div>

        {/* Reviews & Comments Section */}
        <div className="mb-6">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-dive-amber" />
            Reviews & Comments ({barComments.length})
          </h3>

          {/* Add Comment Form */}
          {user ? (
            <form onSubmit={handleSubmitComment} className="mb-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Share your thoughts..."
                  className="flex-1"
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || isSubmitting}
                  className="btn-primary px-4 disabled:opacity-50"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </form>
          ) : (
            <Link
              to="/auth"
              className="block text-center py-3 mb-4 bg-dive-dark border border-dive-gray rounded-lg text-gray-400 hover:text-white hover:border-dive-neon/50 transition-colors"
            >
              Sign in to leave a comment
            </Link>
          )}

          {/* Comments List */}
          <div className="space-y-3">
            {barComments.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">
                No comments yet. Be the first!
              </p>
            ) : (
              barComments.map(comment => (
                <div
                  key={comment.id}
                  className="bg-dive-dark border border-dive-gray rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {/* Avatar */}
                      {comment.userAvatar ? (
                        <img
                          src={comment.userAvatar}
                          alt={comment.userName}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-dive-neon/20 rounded-full flex items-center justify-center">
                          <span className="text-dive-neon text-sm font-bold">
                            {comment.userName?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <Link 
                        to={comment.userId === user?.id ? '/profile' : `/social/${comment.userId}`}
                        className="text-white font-medium text-sm hover:text-dive-neon"
                      >
                        {comment.userUsername ? `@${comment.userUsername}` : comment.userName}
                      </Link>
                      <span className="text-gray-500 text-xs">
                        {new Date(comment.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Edit/Delete for own comments */}
                      {user && user.id === comment.userId && (
                        <>
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-gray-500 hover:text-red-400 text-xs"
                          >
                            Delete
                          </button>
                        </>
                      )}
                      {/* Report for others' comments */}
                      {user && user.id !== comment.userId && (
                        <button
                          onClick={() => setReportingComment(comment)}
                          className="text-gray-500 hover:text-red-400 transition-colors p-1"
                          title="Report comment"
                        >
                          <Flag className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm">{comment.text}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Rating Modal */}
      {showRatingModal && (
        <div ref={ratingModalRef} className="fixed inset-0 bg-black/80 z-50 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="min-h-full flex items-start justify-center p-4 pb-8 md:items-center md:pt-4" style={{ paddingTop: 'calc(4rem + env(safe-area-inset-top, 0px))' }}>
            <div className="bg-dive-dark border border-dive-gray rounded-xl w-full max-w-md">
              <div className="p-4 border-b border-dive-gray flex items-start justify-between gap-2 sticky top-0 bg-dive-dark rounded-t-xl z-10">
                <h3 className="font-display text-xl text-white leading-tight">Rate {bar.name}</h3>
              <button
                onClick={() => {
                  ratingPhotos.forEach(p => URL.revokeObjectURL(p.preview));
                  setRatingPhotos([]);
                  setShowRatingModal(false);
                }}
                className="text-gray-400 hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-6">
              {error && (
                <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Sub-ratings */}
              <div className="space-y-4">
                <div>
                  <label className="text-gray-400 text-sm mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-dive-green" />
                    Price (value for money)
                  </label>
                  <StarRating
                    rating={ratingForm.price}
                    interactive
                    onChange={(val) => setRatingForm(p => ({ ...p, price: val }))}
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 flex items-center gap-2">
                    <Martini className="w-4 h-4 text-dive-blue" />
                    Ambiance
                  </label>
                  <StarRating
                    rating={ratingForm.ambiance}
                    interactive
                    onChange={(val) => setRatingForm(p => ({ ...p, ambiance: val }))}
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4 text-dive-amber" />
                    Crowd
                  </label>
                  <StarRating
                    rating={ratingForm.crowd}
                    interactive
                    onChange={(val) => setRatingForm(p => ({ ...p, crowd: val }))}
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-dive-neon" />
                    Service
                  </label>
                  <StarRating
                    rating={ratingForm.service}
                    interactive
                    onChange={(val) => setRatingForm(p => ({ ...p, service: val }))}
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm mb-2 flex items-center gap-2">
                    <Wine className="w-4 h-4 text-purple-400" />
                    Drink Selection
                  </label>
                  <StarRating
                    rating={ratingForm.drinkSelection}
                    interactive
                    onChange={(val) => setRatingForm(p => ({ ...p, drinkSelection: val }))}
                  />
                </div>
              </div>

              <div className="bg-dive-darker rounded-lg p-4">
                <label className="text-gray-400 text-sm mb-2 block">Overall Rating</label>
                <div className="flex items-center gap-4">
                  <StarRating
                    rating={computeOverallRating(ratingForm)}
                    size="lg"
                  />
                  <span className="text-2xl font-bold text-dive-amber">
                    {computeOverallRating(ratingForm) || '—'}
                  </span>
                </div>
                <p className="text-gray-500 text-xs mt-2">
                  Fills in automatically from the categories above.
                </p>
              </div>

              {/* Dive Bar Vote */}
              <div className="bg-dive-darker rounded-lg p-4">
                <label className="text-white font-medium mb-2 block">
                  Is this a dive bar?
                </label>
                <p className="text-gray-500 text-xs mb-3">
                  Bars with &gt;50% "yes" votes become Community Rated Dive Bars
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setRatingForm(p => ({ ...p, isDiveBar: true }))}
                    className={`flex-1 py-3 rounded-lg border transition-all ${
                      ratingForm.isDiveBar
                        ? 'bg-dive-neon text-white border-dive-neon'
                        : 'bg-dive-dark text-gray-400 border-dive-gray hover:border-dive-light'
                    }`}
                  >
                    🍺 Hell yeah
                  </button>
                  <button
                    type="button"
                    onClick={() => setRatingForm(p => ({ ...p, isDiveBar: false }))}
                    className={`flex-1 py-3 rounded-lg border transition-all ${
                      !ratingForm.isDiveBar
                        ? 'bg-dive-gray text-white border-dive-light'
                        : 'bg-dive-dark text-gray-400 border-dive-gray hover:border-dive-light'
                    }`}
                  >
                    🍸 Nah
                  </button>
                </div>
              </div>

              {/* Add Photos Section */}
              <div className="bg-dive-darker rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-white font-medium flex items-center gap-2">
                    <Camera className="w-4 h-4 text-dive-neon" />
                    Add Photos (Optional)
                  </label>
                  <span className="text-gray-500 text-xs">
                    {ratingPhotos.length}/{MAX_PHOTOS_PER_RATING}
                  </span>
                </div>
                
                {photoError && (
                  <div className="bg-red-500/20 border border-red-500 text-red-400 px-3 py-2 rounded-lg text-xs mb-3">
                    {photoError}
                  </div>
                )}
                
                {/* Photo previews */}
                {ratingPhotos.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                    {ratingPhotos.map(photo => (
                      <div key={photo.id} className="bg-dive-darker rounded-lg p-2 overflow-hidden">
                        {/* Photo with remove button */}
                        <div className="relative group mb-2">
                          <img
                            src={photo.preview}
                            alt="Preview"
                            className="w-full aspect-square object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removeRatingPhoto(photo.id)}
                            className="absolute top-1 right-1 p-1.5 bg-red-500 rounded-full text-white"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        
                        {/* Visibility toggle buttons below the photo */}
                        <div className="grid grid-cols-2 gap-1">
                          <button
                            type="button"
                            onClick={() => updateRatingPhotoVisibility(photo.id, 'public')}
                            className={`flex items-center justify-center gap-1 py-1.5 rounded text-xs font-medium transition-colors ${
                              photo.visibility === 'public' 
                                ? 'bg-dive-neon text-white' 
                                : 'bg-dive-dark text-gray-400 hover:text-white'
                            }`}
                          >
                            <Globe className="w-3 h-3" />
                            <span>Public</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => updateRatingPhotoVisibility(photo.id, 'friends')}
                            className={`flex items-center justify-center gap-1 py-1.5 rounded text-xs font-medium transition-colors ${
                              photo.visibility === 'friends' 
                                ? 'bg-blue-500 text-white' 
                                : 'bg-dive-dark text-gray-400 hover:text-white'
                            }`}
                          >
                            <Users className="w-3 h-3" />
                            <span>Friends</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add photo button */}
                {ratingPhotos.length < MAX_PHOTOS_PER_RATING && (
                  <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-dive-gray rounded-lg cursor-pointer hover:border-dive-neon transition-colors text-gray-400 hover:text-white">
                    <Camera className="w-5 h-5" />
                    <span className="text-sm">
                      {ratingPhotos.length === 0 ? 'Add photos' : 'Add more photos'}
                    </span>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleRatingPhotoSelect}
                      multiple
                      className="hidden"
                    />
                  </label>
                )}
                
                <p className="text-gray-500 text-xs mt-2">
                  <Globe className="w-3 h-3 inline mr-1" />Public = visible on bar profile
                  <span className="mx-2">|</span>
                  <Users className="w-3 h-3 inline mr-1" />Friends = only in friend feed
                </p>
              </div>

              {/* Share Your Experience (Optional) */}
              <div className="bg-dive-darker rounded-lg p-4">
                <label className="text-white font-medium mb-3 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-dive-amber" />
                  Share Your Experience (Optional)
                </label>
                
                {/* Privacy Controls */}
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setModalReviewVisibility('public')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                      modalReviewVisibility === 'public'
                        ? 'bg-dive-neon text-white'
                        : 'bg-dive-dark text-gray-400 hover:text-white border border-dive-gray'
                    }`}
                  >
                    <Globe className="w-3 h-3" />
                    Public
                  </button>
                  <button
                    type="button"
                    onClick={() => setModalReviewVisibility('friends')}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium transition-colors ${
                      modalReviewVisibility === 'friends'
                        ? 'bg-blue-500 text-white'
                        : 'bg-dive-dark text-gray-400 hover:text-white border border-dive-gray'
                    }`}
                  >
                    <Users className="w-3 h-3" />
                    Friends Only
                  </button>
                </div>
                
                {/* Review Text Area */}
                <div className="relative">
                  <textarea
                    value={modalReviewText}
                    onChange={(e) => setModalReviewText(e.target.value.slice(0, 500))}
                    placeholder="What did you think of this place? Share your experience..."
                    className="w-full min-h-[100px] resize-none text-base"
                    rows={4}
                  />
                  <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                    {modalReviewText.length}/500
                  </div>
                </div>
                
                {modalReviewText.length > 0 && modalReviewText.length < 10 && (
                  <p className="text-amber-400 text-xs mt-1">
                    Review must be at least 10 characters
                  </p>
                )}
                
                <p className="text-gray-500 text-xs mt-2">
                  {modalReviewVisibility === 'public' 
                    ? '🌐 Public reviews appear on the bar profile' 
                    : '👥 Friends-only reviews only appear in friend feeds'}
                </p>
              </div>
            </div>

            <div className="p-4 border-t border-dive-gray flex gap-3">
              <button
                onClick={() => {
                  // Clean up photo previews
                  ratingPhotos.forEach(p => URL.revokeObjectURL(p.preview));
                  setRatingPhotos([]);
                  setShowRatingModal(false);
                }}
                className="btn-secondary flex-1"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRating}
                disabled={isSubmitting || !hasAnySubRating(ratingForm)}
                className="btn-primary flex-1 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {uploadingPhotos ? 'Uploading photos...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    Submit Rating
                    {ratingPhotos.length > 0 && (
                      <span className="text-xs opacity-75">
                        + {ratingPhotos.length} photo{ratingPhotos.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Add to List Modal */}
      {showListModal && (
        <AddToListModal
          barId={bar.id}
          barName={bar.name}
          onClose={() => setShowListModal(false)}
        />
      )}

      {/* Share Review Modal */}
      <ShareReviewModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        bar={bar}
        rating={lastSubmittedRating || userRating || { overall: 0 }}
        user={user}
        profile={profile}
      />

      {/* Report Comment Modal */}
      <ReportModal
        isOpen={!!reportingComment}
        onClose={() => setReportingComment(null)}
        contentType="comment"
        contentId={reportingComment?.id}
        contentPreview={reportingComment?.text}
      />
    </div>
  );
}

export default BarDetail;
